<template>
    <div class="uom-page">
        <h1 class="title">
            <b-icon icon="ruler" />
            Unit of Measure
        </h1>

        <b-message
            v-if="loadError"
            type="is-danger"
            :closable="false">
            Could not load the Unit of Measure data: {{ loadError }}
        </b-message>

        <spinner :loading="loading">
            <div
                v-if="!loading && !loadError"
                class="columns">
                <!-- Quantity list -->
                <div class="column is-one-quarter">
                    <b-field>
                        <b-input
                            v-model="search"
                            placeholder="Search quantities and units"
                            type="search"
                            icon="magnify"
                            icon-right="close-circle"
                            icon-right-clickable
                            @icon-right-click="search = ''" />
                    </b-field>
                    <p class="has-text-grey mb-2">
                        {{ filteredQuantities.length }} of {{ quantities.length }} quantities
                    </p>
                    <ul class="quantity-list">
                        <li
                            v-for="q in filteredQuantities"
                            :key="q.identity">
                            <router-link
                                class="quantity-item"
                                :class="{ 'is-active': q.identity === selectedIdentity }"
                                :to="quantityRoute(q.identity)">
                                <span class="quantity-name">{{ q.name }}</span>
                                <span class="quantity-meta">
                                    <b-tag
                                        v-if="q.isBaseQuantity"
                                        type="is-info is-light"
                                        title="Base quantity">base</b-tag>
                                    <span class="has-text-grey">{{ unitCount(q.identity) }}</span>
                                </span>
                                <span
                                    v-if="unitMatch(q.identity)"
                                    class="has-text-grey is-italic unit-hint">
                                    matches {{ unitMatch(q.identity) }}
                                </span>
                            </router-link>
                        </li>
                    </ul>
                    <p
                        v-if="filteredQuantities.length === 0"
                        class="has-text-grey is-italic">
                        No quantity or unit matches “{{ search }}”.
                    </p>
                </div>

                <!-- Detail -->
                <div class="column">
                    <b-message
                        v-if="!selectedQuantity && selectedIdentity"
                        type="is-warning"
                        :closable="false">
                        Quantity “{{ selectedIdentity }}” was not found. The link may be invalid
                        or out of date — pick a quantity from the list to continue.
                    </b-message>
                    <div
                        v-else-if="!selectedQuantity"
                        class="has-text-grey is-italic">
                        Select a quantity to see its definition and units.
                    </div>

                    <template v-else>
                        <div class="box">
                            <h2 class="subtitle mb-2">
                                {{ selectedQuantity.name }}
                                <b-tag
                                    v-if="selectedQuantity.isBaseQuantity"
                                    type="is-info is-light">
                                    base quantity
                                </b-tag>
                            </h2>
                            <p
                                v-if="selectedQuantity.description"
                                class="mb-3">
                                {{ selectedQuantity.description }}
                            </p>
                            <table class="table is-narrow is-fullwidth detail-table">
                                <tbody>
                                    <tr v-if="selectedQuantity.definition">
                                        <th>Definition</th>
                                        <td>{{ selectedQuantity.definition }}</td>
                                    </tr>
                                    <tr>
                                        <th>Core unit</th>
                                        <td>
                                            <span v-if="coreUnit">
                                                <strong>{{ coreUnit.name }}</strong>
                                                <span v-if="coreUnit.description"> — {{ coreUnit.description }}</span>
                                            </span>
                                            <span
                                                v-else
                                                class="has-text-grey is-italic">not set</span>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedQuantity.dimension">
                                        <th>Dimension</th>
                                        <td>
                                            <code>{{ selectedQuantity.dimension }}</code>
                                            <span
                                                v-if="sharedDimensionQuantities.length"
                                                class="has-text-grey">
                                                — also used by
                                                <router-link
                                                    v-for="(other, i) in sharedDimensionQuantities"
                                                    :key="other.identity"
                                                    :to="quantityRoute(other.identity)">{{ other.name }}{{ i < sharedDimensionQuantities.length - 1 ? ', ' : '' }}</router-link>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedQuantity.alternateNames">
                                        <th>Alternate names</th>
                                        <td>{{ selectedQuantity.alternateNames }}</td>
                                    </tr>
                                    <tr v-if="selectedQuantity.remark">
                                        <th>Remark</th>
                                        <td>{{ selectedQuantity.remark }}</td>
                                    </tr>
                                    <tr v-if="selectedQuantity.iec61360Code">
                                        <th>IEC 61360</th>
                                        <td>{{ selectedQuantity.iec61360Code }}</td>
                                    </tr>
                                    <tr v-if="selectedQuantity.iso15926Url">
                                        <th>ISO 15926</th>
                                        <td>
                                            <a
                                                :href="selectedQuantity.iso15926Url"
                                                target="_blank"
                                                rel="noopener">{{ selectedQuantity.iso15926Url }}</a>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Converter -->
                        <div
                            v-if="convertibleUnits.length > 1"
                            class="box">
                            <h3 class="is-size-6 has-text-weight-semibold mb-2">
                                Convert
                            </h3>
                            <div class="columns is-vcentered is-variable is-2">
                                <div class="column is-narrow">
                                    <b-input
                                        v-model="converterInput"
                                        type="number"
                                        step="any"
                                        placeholder="Value"
                                        class="converter-value" />
                                </div>
                                <div class="column is-narrow">
                                    <b-select v-model="converterUnitIdentity">
                                        <option
                                            v-for="u in convertibleUnits"
                                            :key="u.identity"
                                            :value="u.identity">
                                            {{ u.name }}
                                        </option>
                                    </b-select>
                                </div>
                                <div class="column has-text-grey">
                                    equals the values shown in the “Converted” column below
                                </div>
                            </div>
                        </div>

                        <!-- Units -->
                        <div
                            v-if="unitsForQuantity.length === 0"
                            class="has-text-grey is-italic">
                            This quantity has no valid units.
                        </div>
                        <div
                            v-for="group in groupedUnits"
                            :key="group.system"
                            class="mb-5">
                            <h3 class="is-size-6 has-text-weight-semibold mb-1">
                                {{ group.system }}
                                <span class="has-text-grey has-text-weight-normal">
                                    ({{ group.units.length }})
                                    <span v-if="systemDescription(group.system)">— {{ systemDescription(group.system) }}</span>
                                </span>
                            </h3>
                            <b-table
                                :data="group.units"
                                :striped="true"
                                :narrowed="true"
                                :hoverable="true">
                                <b-table-column
                                    v-slot="{ row }"
                                    field="name"
                                    label="Unit"
                                    sortable>
                                    <strong>{{ row.name }}</strong>
                                    <b-tag
                                        v-if="row.name === coreUnitName"
                                        type="is-primary is-light"
                                        class="ml-1">
                                        core
                                    </b-tag>
                                </b-table-column>
                                <b-table-column
                                    v-slot="{ row }"
                                    field="description"
                                    label="Description"
                                    sortable>
                                    {{ row.description }}
                                </b-table-column>
                                <b-table-column
                                    v-slot="{ row }"
                                    label="Converted"
                                    numeric
                                    :visible="convertibleUnits.length > 1 && converterValue !== null">
                                    <span class="is-family-monospace">{{ convertedText(row) }}</span>
                                </b-table-column>
                                <b-table-column
                                    v-slot="{ row }"
                                    label="Conversion">
                                    <span
                                        v-if="row.hasConversion || row.name === coreUnitName"
                                        class="is-family-monospace">
                                        {{ formula(row) }}
                                    </span>
                                    <b-tag
                                        v-else
                                        type="is-warning is-light">
                                        missing
                                    </b-tag>
                                </b-table-column>
                                <b-table-column
                                    v-slot="{ row }"
                                    label="Symbols">
                                    <b-taglist v-if="symbolsOf(row).length">
                                        <b-tag
                                            v-for="symbol in symbolsOf(row)"
                                            :key="symbol"
                                            class="is-family-monospace">
                                            {{ symbol }}
                                        </b-tag>
                                    </b-taglist>
                                </b-table-column>
                                <b-table-column
                                    v-slot="{ row }"
                                    label="Flags">
                                    <b-tag
                                        v-if="row.isDefaultForPrint"
                                        type="is-light"
                                        class="mr-1"
                                        title="Default unit for print">
                                        print
                                    </b-tag>
                                    <b-tag
                                        v-if="row.isDefaultForTransport"
                                        type="is-light"
                                        class="mr-1"
                                        title="Default unit for transport">
                                        transport
                                    </b-tag>
                                    <b-tag
                                        v-if="row.isCfihos"
                                        type="is-light"
                                        class="mr-1"
                                        title="Used by CFIHOS">
                                        CFIHOS
                                    </b-tag>
                                    <b-tag
                                        v-if="row.isInBaseUnits"
                                        type="is-light"
                                        class="mr-1"
                                        title="Expressed in SI base units">
                                        SI base
                                    </b-tag>
                                    <b-tag
                                        v-if="row.aliasOfUoM"
                                        type="is-light"
                                        class="mr-1"
                                        :title="'Alias of ' + unitNameOf(row.aliasOfUoM)">
                                        = {{ unitNameOf(row.aliasOfUoM) }}
                                    </b-tag>
                                    <b-tag
                                        v-if="row.hasConversion && row.isQualityChecked"
                                        type="is-success is-light"
                                        title="Conversion factor has been quality checked">
                                        checked
                                    </b-tag>
                                </b-table-column>
                            </b-table>
                        </div>
                    </template>
                </div>
            </div>
        </spinner>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import type { RouteLocationRaw } from 'vue-router';
    import Spinner from '@/shared/components/Spinner.vue';
    import {
        conversionFormula,
        convert,
        formatNumber,
        loadUomData,
        parseNumericInput,
        parseSymbols,
        unitNameOf,
        type Quantity,
        type UnitWithConversion
    } from './uomData';

    const props = defineProps<{ quantityIdentity?: string | null }>();

    const loading = ref(true);
    const loadError = ref<string | null>(null);
    const quantities = ref<Quantity[]>([]);
    const units = ref<UnitWithConversion[]>([]);
    const measureSystems = ref<Map<string, string | null>>(new Map());

    const search = ref('');
    // Pre-filled so the converted column is populated as soon as a quantity opens.
    const converterInput = ref<string | number>(1);
    const converterUnitIdentity = ref<string | null>(null);

    // The URL is the single source of truth for the selection, so that deep links,
    // browser history and opening a quantity in a new tab all behave the same.
    const selectedIdentity = computed(() => props.quantityIdentity ?? null);

    function quantityRoute(identity: string): RouteLocationRaw {
        return { name: 'Quantity', params: { quantityIdentity: identity } };
    }

    const unitsByQuantity = computed(() => {
        const map = new Map<string, UnitWithConversion[]>();
        for (const u of units.value) {
            if (!u.quantity) continue;
            const list = map.get(u.quantity);
            if (list) list.push(u);
            else map.set(u.quantity, [u]);
        }
        return map;
    });

    const selectedQuantity = computed(() =>
        quantities.value.find(q => q.identity === selectedIdentity.value) ?? null);

    const unitsForQuantity = computed(() =>
        selectedQuantity.value ? unitsByQuantity.value.get(selectedQuantity.value.identity) ?? [] : []);

    const coreUnitName = computed(() => unitNameOf(selectedQuantity.value?.coreUoM ?? null));

    const coreUnit = computed(() =>
        unitsForQuantity.value.find(u => u.name === coreUnitName.value) ?? null);

    /** Quantities sharing this one's dimension — a known source of duplicates. */
    const sharedDimensionQuantities = computed(() => {
        const dimension = selectedQuantity.value?.dimension;
        if (!dimension) return [];
        return quantities.value.filter(q =>
            q.dimension === dimension && q.identity !== selectedQuantity.value?.identity);
    });

    const searchTerm = computed(() => search.value.trim().toLowerCase());

    /**
     * Quantity identity -> the name of a unit matching the search, but only for
     * quantities whose own name does not match. Those are the ones where the
     * reader needs telling why the quantity is in the list at all.
     */
    const unitMatchesByQuantity = computed(() => {
        const map = new Map<string, string>();
        const term = searchTerm.value;
        if (!term) return map;
        const selfMatching = new Set(quantities.value
            .filter(q => q.name.toLowerCase().includes(term))
            .map(q => q.identity));
        for (const u of units.value) {
            if (!u.quantity || map.has(u.quantity) || selfMatching.has(u.quantity)) continue;
            if (matchesUnit(u, term)) map.set(u.quantity, u.name);
        }
        return map;
    });

    function matchesUnit(u: UnitWithConversion, term: string): boolean {
        return u.name.toLowerCase().includes(term)
            || (u.description?.toLowerCase().includes(term) ?? false)
            || (u.alternateSymbols?.toLowerCase().includes(term) ?? false);
    }

    const filteredQuantities = computed(() => {
        const term = searchTerm.value;
        if (!term) return quantities.value;
        return quantities.value.filter(q =>
            q.name.toLowerCase().includes(term)
            || (q.description?.toLowerCase().includes(term) ?? false)
            || (q.alternateNames?.toLowerCase().includes(term) ?? false)
            || (q.dimension?.toLowerCase().includes(term) ?? false)
            || unitMatchesByQuantity.value.has(q.identity));
    });

    const groupedUnits = computed(() => {
        const groups = new Map<string, UnitWithConversion[]>();
        for (const u of unitsForQuantity.value) {
            const key = u.measureSystem ?? 'No measure system';
            const list = groups.get(key);
            if (list) list.push(u);
            else groups.set(key, [u]);
        }
        return [...groups.entries()]
            .map(([system, list]) => ({
                system,
                units: [...list].sort((a, b) => a.name.localeCompare(b.name))
            }))
            .sort((a, b) => a.system.localeCompare(b.system));
    });

    const convertibleUnits = computed(() =>
        unitsForQuantity.value
            .filter(u => u.factorA !== null)
            .sort((a, b) => a.name.localeCompare(b.name)));

    const converterValue = computed(() => parseNumericInput(converterInput.value));

    const converterUnit = computed(() =>
        convertibleUnits.value.find(u => u.identity === converterUnitIdentity.value) ?? null);

    function unitCount(quantityIdentity: string): number {
        return unitsByQuantity.value.get(quantityIdentity)?.length ?? 0;
    }

    function unitMatch(quantityIdentity: string): string | undefined {
        return unitMatchesByQuantity.value.get(quantityIdentity);
    }

    function systemDescription(system: string): string | null {
        return measureSystems.value.get(system) ?? null;
    }

    function formula(unit: UnitWithConversion): string {
        return conversionFormula(unit, coreUnitName.value);
    }

    function symbolsOf(unit: UnitWithConversion): string[] {
        return parseSymbols(unit.alternateSymbols);
    }

    function convertedText(unit: UnitWithConversion): string {
        const value = converterValue.value;
        const from = converterUnit.value;
        if (value === null || !from) return '';
        if (unit.factorA === null) return '—';
        const result = convert(from, unit, value);
        if (result === null || !Number.isFinite(result)) return '—';
        return formatNumber(result);
    }

    // Keep the converter pointed at a unit that belongs to the selected quantity.
    watch(unitsForQuantity, () => {
        const stillValid = convertibleUnits.value.some(u => u.identity === converterUnitIdentity.value);
        if (!stillValid) {
            const preferred = convertibleUnits.value.find(u => u.name === coreUnitName.value)
                ?? convertibleUnits.value[0];
            converterUnitIdentity.value = preferred?.identity ?? null;
        }
    });

    onMounted(async () => {
        try {
            const data = await loadUomData();
            quantities.value = data.quantities;
            units.value = data.units;
            measureSystems.value = new Map(data.measureSystems.map(s => [s.name, s.description]));
        } catch (e) {
            loadError.value = e instanceof Error ? e.message : String(e);
        } finally {
            loading.value = false;
        }
    });
</script>

<style scoped>
.quantity-list {
    max-height: calc(100vh - 16rem);
    overflow-y: auto;
    border-top: 1px solid var(--eq-autumn-storm-1);
}

.quantity-item {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.25rem;
    padding: 0.3rem 0.5rem;
    border-bottom: 1px solid var(--eq-autumn-storm-2);
    color: inherit;
}

.quantity-item:hover {
    background-color: var(--eq-autumn-storm-3);
}

.quantity-item.is-active {
    background-color: var(--eq-bg-highlight);
    font-weight: 600;
}

.quantity-name {
    word-break: break-word;
}

.quantity-meta {
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
}

.unit-hint {
    flex-basis: 100%;
}

.detail-table th {
    width: 10rem;
    white-space: nowrap;
    vertical-align: top;
    font-weight: 600;
}

.converter-value {
    width: 10rem;
}
</style>
